<script lang="ts" setup>
import Menu from 'primevue/menu'
import type { CartItem } from '~/types/commerce'
import { useItemDisplay } from '~/composables/useItemDisplay'
import { useCommerceStore } from '~/stores/pos/commerce'

interface Props {
  item: CartItem
  context?: 'cart' | 'order'
  showActions?: boolean
  showImage?: boolean
  compactView?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  context: 'cart',
  showActions: false,
  showImage: false,
  compactView: false,
})

const emit = defineEmits<{
  increase: [item: CartItem]
  decrease: [item: CartItem]
  edit: [cartUuid: string]
  remove: [cartUuid: string]
}>()

const { t } = useNuxtApp().$i18n
const menu = ref()

const commerceStore = useCommerceStore()

const {
  hasModifierSelections,
  getModifierSelections,
  shouldShowQuantityMultiplication,
  formatQuantityMultiplication,
  getComboProductModifiers,
} = useItemDisplay()

const menuItems = computed(() => [
  {
    label: t('Add one'),
    icon: 'pi pi-plus',
    command: () => emit('increase', props.item),
  },
  {
    label: t('Remove one'),
    icon: 'pi pi-minus',
    command: () => emit('decrease', props.item),
  },
  {
    separator: true,
  },
  {
    label: t('Edit'),
    icon: 'pi pi-pencil',
    command: () => emit('edit', props.item.cart_uuid),
  },
  {
    label: t('Remove'),
    icon: 'pi pi-trash',
    class: 'text-red-500',
    command: () => emit('remove', props.item.cart_uuid),
  },
])

const toggleMenu = (event: Event) => {
  menu.value.toggle(event)
}

// Get combo data based on context
const comboData = computed(() => {
  if (props.context === 'order') {
    return props.item.combo_data || props.item
  }
  return props.item
})

// Get combo items based on context
const comboItems = computed(() => {
  if (props.context === 'order') {
    return props.item.combo_data?.items || []
  }
  return props.item.items || []
})

// Calculate the actual total including price variations
const calculateOrderTotal = computed(() => {
  if (props.context !== 'order') {
    return commerceStore.calculateCartItemTotal(props.item) || 0
  }

  // For orders, check if we need to calculate the total
  // First, let's see if we have a base price per unit in combo_data
  const basePrice = props.item.combo_data?.base_price || props.item.base_price || 85

  // Calculate total variations per unit
  let variationsPerUnit = 0
  const items = props.item.combo_data?.items || []
  items.forEach((item: any) => {
    // Add price variations
    if (item.price_variation && item.price_variation > 0) {
      variationsPerUnit += item.price_variation * (item.quantity || 1)
    }

    // Add modifier prices
    if (item.modifiers && Array.isArray(item.modifiers)) {
      item.modifiers.forEach((modifier: any) => {
        if (modifier.selected) {
          const selections = Array.isArray(modifier.selected) ? modifier.selected : [modifier.selected]
          selections.forEach((selection: any) => {
            const priceChange = selection.price_modifier || selection.price_change || 0
            if (priceChange > 0) {
              variationsPerUnit += priceChange * (item.quantity || 1)
            }
          })
        }
      })
    }

    // Handle selected_modifiers structure
    if (item.selected_modifiers && Object.keys(item.selected_modifiers).length > 0) {
      Object.values(item.selected_modifiers).forEach((selections: any) => {
        if (Array.isArray(selections)) {
          selections.forEach((selection: any) => {
            const priceChange = selection.price_modifier || selection.price_change || 0
            if (priceChange > 0) {
              variationsPerUnit += priceChange * (item.quantity || 1)
            }
          })
        }
      })
    }
  })

  // Calculate price per unit (base + variations)
  const pricePerUnit = basePrice + variationsPerUnit

  // Return total for all quantities
  return pricePerUnit * (props.item.quantity || 1)
})

// Calculate price per unit (base price without variations)
const pricePerUnit = computed(() => {
  if (props.context === 'order') {
    // For orders, use the base price from combo_data or fallback to base_price
    // Never use the price field here as it includes variations
    return props.item.combo_data?.base_price || props.item.base_price || 85
  }
  // For cart, use the base price from the item
  return props.item.base_price || 0
})

// Compact display: show combo items as a single line
const compactItemsText = computed(() => {
  const items = comboItems.value.map((product: any) => product.product_name).join(', ')
  return items.length > 0 ? ` - ${items}` : ''
})
</script>

<template>
  <div class="custom-combo-item-renderer">
    <!-- Compact View -->
    <div
      v-if="compactView"
      class="flex items-center gap-2"
    >
      <div class="grow text-sm">
        <span class="font-semibold">
          <template v-if="item.quantity > 1">{{ item.quantity }} x </template>{{ comboData.name }} - ({{ $t('Custom') }})
        </span>
        <span class="text-gray-600">{{ compactItemsText }}</span>
        <span
          v-if="item.notes"
          class="text-gray-500 italic ml-2"
        >📝 {{ item.notes }}</span>
      </div>
      <div class="flex items-center gap-2">
        <span class="font-semibold text-sm whitespace-nowrap">
          {{ usePriceFormatter(context === 'order' ? calculateOrderTotal : (commerceStore.calculateCartItemTotal(item) || 0)) }}
        </span>
        <Button
          icon="pi pi-ellipsis-v"
          class="p-button-text p-button-rounded !p-1"
          size="small"
          :aria-label="t('Actions')"
          @click="toggleMenu"
        />
        <Menu
          ref="menu"
          :model="menuItems"
          :popup="true"
        />
      </div>
    </div>

    <!-- Normal View -->
    <div
      v-else
      class="flex gap-2"
    >
      <!-- Combo Image -->
      <div
        v-if="showImage && item.images"
        class="flex-shrink-0"
      >
        <img
          class="size-24 rounded-md"
          :src="getFirstImage(item) || '/product_placeholder.webp'"
          :alt="`Image of ${comboData.name}`"
        >
      </div>

      <!-- Combo Content -->
      <div class="grow">
        <!-- Header with name and price -->
        <div class="flex text-lg">
          <div class="grow">
            <span class="font-semibold">
              {{ item.quantity }} x {{ comboData.name }} - ({{ $t('Custom') }})
            </span>
          </div>
          <div class="flex">
            <span v-if="item.quantity">{{ item.quantity }} x </span>
            <span class="ml-2">
              {{ usePriceFormatter(pricePerUnit) }}
            </span>
          </div>
        </div>

        <!-- Custom Combo Items -->
        <ul
          v-if="comboItems.length > 0"
          class="list-none p-0 pl-6 mt-2"
        >
          <li
            v-for="product in comboItems"
            :key="product.id || product.product_id"
            class="flex justify-between text-sm mb-1"
          >
            <div class="text-left">
              <span>{{ product.quantity }} x </span>
              <span>{{ product.product_name }}</span>

              <!-- Price variation -->
              <span
                v-if="product.price_variation && product.price_variation > 0"
                class="text-black ml-2 font-semibold"
              >
                + {{ usePriceFormatter(product.price_variation) }}
              </span>

              <!-- Product Modifiers -->
              <div v-if="getComboProductModifiers(product)">
                <!-- Handle selected_modifiers (object structure) -->
                <div
                  v-if="product.selected_modifiers && Object.keys(product.selected_modifiers).length > 0"
                  class="pl-5 mt-1"
                >
                  <template
                    v-for="(selections, modifierId) in product.selected_modifiers"
                    :key="modifierId"
                  >
                    <div v-if="Array.isArray(selections) && selections.length > 0">
                      <div
                        v-for="selection in selections"
                        :key="selection.id"
                        class="pl-1"
                      >
                        <span>+ {{ selection.name }}</span>
                        <span
                          v-if="selection.price_modifier > 0"
                          class="font-semibold text-black"
                        >
                          + {{ usePriceFormatter(selection.price_modifier) }}
                          <span v-if="shouldShowQuantityMultiplication('CUSTOM_COMBO', item.quantity, product.quantity)">
                            {{ formatQuantityMultiplication(selection.price_modifier, product.quantity) }}
                          </span>
                        </span>
                      </div>
                    </div>
                  </template>
                </div>

                <!-- Handle modifiers array structure -->
                <div
                  v-else-if="product.modifiers && product.modifiers.some((m: any) => hasModifierSelections(m))"
                  class="pl-5 mt-1"
                >
                  <template
                    v-for="modifier in product.modifiers"
                    :key="modifier.group_id"
                  >
                    <template v-if="hasModifierSelections(modifier)">
                      <div
                        v-for="selection in getModifierSelections(modifier)"
                        :key="selection.id"
                        class="pl-1"
                      >
                        <span>+ {{ selection.name }}</span>
                        <span
                          v-if="(selection.price_modifier || selection.price_change || 0) > 0"
                          class="font-semibold text-black"
                        >
                          + {{ usePriceFormatter(selection.price_modifier || selection.price_change || 0) }}
                          <span v-if="shouldShowQuantityMultiplication('CUSTOM_COMBO', item.quantity, product.quantity)">
                            {{ formatQuantityMultiplication(selection.price_modifier || selection.price_change || 0, product.quantity) }}
                          </span>
                        </span>
                      </div>
                    </template>
                  </template>
                </div>
              </div>

              <!-- Legacy extras/option_name display -->
              <div
                v-if="product.option_name"
                class="pl-5 text-xs text-gray-600"
              >
                ({{ product.option_name }})
              </div>
            </div>
          </li>
        </ul>

        <!-- Total -->
        <div
          class="mt-2 pt-2 text-right text-lg border-t border-gray-300"
        >
          <span class="font-semibold">
            {{ usePriceFormatter(calculateOrderTotal) }}
          </span>
        </div>

        <!-- Action Buttons -->
        <div
          v-if="showActions"
          class="mt-2 flex justify-end gap-1"
        >
          <Button
            class="p-button-rounded p-button-danger p-2"
            icon="pi pi-minus"
            :aria-label="$t('Remove one')"
            @click="emit('decrease', item)"
          />
          <Button
            class="p-button-rounded border-green-500 bg-green-500 p-2 hover:border-green-700"
            icon="pi pi-plus"
            :aria-label="$t('Add one')"
            @click="emit('increase', item)"
          />
          <Button
            icon="pi pi-pencil"
            class="p-button-rounded p-2"
            :aria-label="$t('Edit Custom Combo')"
            @click="emit('edit', item.cart_uuid)"
          />
          <Button
            icon="pi pi-trash"
            class="p-button-rounded p-button-danger p-2"
            :aria-label="$t('Remove')"
            @click="emit('remove', item.cart_uuid)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.custom-combo-item-renderer {
  @apply w-full;
}

/* Print styles */
@media print {
  .custom-combo-item-renderer {
    @apply text-xs;
  }

  .custom-combo-item-renderer .text-lg {
    @apply text-xs;
  }

  .custom-combo-item-renderer .text-sm {
    @apply text-xs;
  }

  .custom-combo-item-renderer ul {
    @apply pl-2;
  }
}
</style>
